import React from 'react';
import { Card } from '../../ui/Card';
import { motion } from 'framer-motion';
import { TrendingUp } from 'lucide-react';

interface DataPoint {
  date: string;
  value: number;
}

interface LineChartProps {
  title: string;
  data: DataPoint[];
  color: string;
}

export const LineChart: React.FC<LineChartProps> = ({ title, data, color }) => {
  const maxValue = Math.max(...data.map(d => d.value), 1); // Ensure minimum maxValue of 1
  const points = data.map((d, i) => ({
    x: (i / (data.length - 1)) * 100,
    y: maxValue === 0 ? 50 : 100 - (d.value / maxValue) * 100, // Center line when no data
  }));

  const pathData = points.length > 1 
    ? `M ${points.map(p => `${p.x},${p.y}`).join(' L ')}` 
    : '';

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <div className={`p-2 ${color} bg-opacity-10 rounded-lg`}>
          <TrendingUp className={`w-5 h-5 ${color.replace('bg-', 'text-')}`} />
        </div>
        <h2 className="text-lg font-semibold">{title}</h2>
      </div>
      
      <div className="relative h-64">
        {points.length > 1 ? (
          <>
            <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
              {/* Grid lines */}
              <g className="stroke-gray-200" strokeWidth="0.5">
                <line x1="0" y1="25" x2="100" y2="25" />
                <line x1="0" y1="50" x2="100" y2="50" />
                <line x1="0" y1="75" x2="100" y2="75" />
              </g>
              
              {/* Activity line */}
              <motion.path
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 1.5, ease: "easeInOut" }}
                d={pathData}
                fill="none"
                stroke={color.replace('bg-', 'rgb').replace('-500', '')}
                strokeWidth="2"
                vectorEffect="non-scaling-stroke"
              />
            </svg>
            
            <div className="absolute inset-0 flex items-end justify-between px-4">
              {data.map((point, i) => (
                <div key={i} className="flex flex-col items-center">
                  <span className="text-xs font-medium text-gray-600">{point.value}</span>
                  <span className="text-xs text-gray-400 mt-1">{point.date}</span>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="flex items-center justify-center h-full text-gray-500">
            No activity data available
          </div>
        )}
      </div>
    </div>
  );
};